'use strict';

import axios from 'axios';
import { listenDOMEvent } from '@jspartials/dom-api-utils';
import { classes, hasClassElement } from '@jspartials/class-list-utils';

// Attributes
const attribute = {
  gtmListName: 'gtm-list-name'
};

// Selectors
const selector = {
  customerAreaPage: '.gtm-customer-area-page',
  productTile: '.gtm-product-tile',
  productName: '.gtm-product-name',
  productPrice: '.gtm-product-price',
  productTileLink: '.gtm-product-tile__link',
  productTileVariantsList: '.gtm-product-tile__variants-list',
  productTileVariant: '.gtm-product-tile__variants-list-item',
  productTileAddToCart: '.js-add-to-cart',
  productVariant: '.gtm-product-variant',
  gtmList: '.gtm-list',
  gtmListName: `[${attribute.gtmListName}]`
};

// Elements
const customerAreaPage = document.querySelector(selector.customerAreaPage);

// Variables
const productListDefaultName = 'product-list';
const urlToGetInfoOfProduct = globalVariables.endpoint.gtmGetInfoProduct;
const urlToGetInfoOfCart = globalVariables.endpoint.gtmGetInfoCart;
let listOfProductTilesObserved = [];
let listOfProductTileVariantsViewed = [];
let listOfProductVariantsObserved = [];
let observer = null;
const optionsForIntersectionObserver = {
  root: null,
  threshold: 1
};
const customEvent = {
  listenProductTiles: 'gtmListenProductTiles',
  afterAddToCart: 'gtmAfterAddToCart',
  afterRemoveFromCart: 'gtmAfterRemoveFromCart',
  afterAddToWishlit: 'gtmAfterAddToWishList',
  afterRemoveFromWishlist: 'gtmAfterRemoveFromWishList',
  clickButtonShowMoreResults: 'gtmClickButtonShowMoreResults',
  afterLogin: 'customerLoggedCorrectly',
  previousLoggedSocially: 'customerPreviousLoggedSocially',
  addShippingInfo: 'gtmAddShippingInfo',
  addPaymentInfo: 'gtmAddPaymentInfo'
};
const gtmEventName = {
  eventTracking: 'eventTracking',
  login: 'login',
  signUp: 'sign_up',
  addToCart: 'add_to_cart',
  removeFromCart: 'remove_from_cart',
  viewItemList: 'view_item_list',
  viewItem: 'view_item',
  selectItem: 'select_item',
  addToWishlist: 'add_to_wishlist',
  removeFromWishlist: 'remove_from_wishlist',
  applyFilter: 'apply_filter',
  applySorting: 'apply_sorting'
};
const gtmActionName = {
  add: 'add',
  remove: 'remove',
  click: 'click',
  view: 'view'
};
const gtmCategoryName = {
  moreResults: 'More Results',
  header: 'Header',
  login: 'UserAreaLogin',
  account: 'UserArea',
  footer: 'Footer'
};
const productType = {
  variant: 'variant',
  tile: 'tile'
};
let listOfProductTilesViewedAccumulated = [];
const waitingTimeToLaunchViewProductTile = 2000; // milliseconds
let accumulationOfProductTilesViewedIsRunning = false;
let listOfProductVariantsViewedAccumulated = [];
const waitingTimeToLaunchViewProductVariant = 2000; // milliseconds
let accumulationOfProductVariantsViewedIsRunning = false;

// Functions

const waitFor = (condition, step = 1000, timeout = Infinity) => {
  return new Promise((resolve, reject) => {
    const now = Date.now();

    const interval = setInterval(async () => {
      const result = await condition();

      if (result) {
        clearInterval(interval);
        resolve(result);
      } else if (Date.now() - now >= timeout * 1000) {
        clearInterval(interval);
        reject(result);
      }
    }, step);
  });
};

const canLaunchViewProductTile = () => {
  return (
    !accumulationOfProductTilesViewedIsRunning &&
    listOfProductTilesViewedAccumulated.length > 0
  );
};

const canLaunchViewProductVariant = () => {
  return (
    !accumulationOfProductVariantsViewedIsRunning &&
    listOfProductVariantsViewedAccumulated.length > 0
  );
};

/**
 * Initialize the dataLayer if it doesn't exist
 */
const initGtmDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Push data to the dataLayer
 * @param {Object} data - Data to be pushed to the dataLayer
 * @returns {void}
 */
const pushOnDataLayer = (data) => {
  initGtmDataLayer();
  window.dataLayer.push(data);
};

const getPosition = (element, list) =>
  Array.prototype.indexOf.call(list, element) + 1;

const getListName = (product) => {
  const gtmListNameElement = product.closest(selector.gtmListName);
  return gtmListNameElement
    ? gtmListNameElement.getAttribute(attribute.gtmListName)
    : productListDefaultName;
};

const accumulateViewProductTile = (product, list) => {
  const productId = product.dataset.pid || null;

  if (productId) {
    getInfoProduct(productId).then((productData) => {
      productData.list_id = getListName(product);
      productData.index = getPosition(product, list);
      productData.quantity = 1;

      listOfProductTilesViewedAccumulated.push(productData);
    });
  }
};

async function launchViewProductTile() {
  accumulationOfProductTilesViewedIsRunning = true;

  setTimeout(
    () => (accumulationOfProductTilesViewedIsRunning = false),
    waitingTimeToLaunchViewProductTile
  );

  await waitFor(canLaunchViewProductTile).then(function () {
    pushOnDataLayer({
      event: gtmEventName.viewItemList,
      ecommerce: {
        items: listOfProductTilesViewedAccumulated
      }
    });

    listOfProductTilesViewedAccumulated = [];
    accumulationOfProductTilesViewedIsRunning = false;
  });
}

const launchAddToCartProduct = (productId, quantity, list) => {
  getInfoProduct(productId)
    .then((productData) => {
      const actionFieldData = list ? { list: list } : {};

      productData.quantity = quantity;
      productData.list_id = list || '';

      getInfoCart()
        .then((cartData) => {
          launchAddToCart(productData, cartData, actionFieldData);
        })
        .catch((error) => {
          console.error(error);
        });
    })
    .catch((error) => {
      console.error(error);
    });
};

const launchRemoveFromCartProduct = (productId, quantity, list) => {
  getInfoProduct(productId)
    .then((productData) => {
      const actionFieldData = list ? { list: list } : {};

      productData.quantity = quantity;
      productData.list_id = list || '';

      getInfoCart()
        .then((cartData) => {
          launchRemoveFromCart(productData, cartData, actionFieldData);
        })
        .catch((error) => {
          console.error(error);
        });
    })
    .catch((error) => {
      console.error(error);
    });
};

const launchAddProductToWishlist = (productId) => {
  getInfoProduct(productId).then((productData) => {
    const actionFieldData = {};

    launchAddToWishlist(productData, actionFieldData);
  });
};

const launchRemoveProductFromWishlist = (productId) => {
  getInfoProduct(productId).then((productData) => {
    const actionFieldData = {};

    launchRemoveFromWishlist(productData, actionFieldData);
  });
};

const launchClickProductTile = (product, list, url) => {
  const productId = product.dataset.pid || null;

  if (productId) {
    getInfoProduct(productId).then((productData) => {
      productData.list_id = getListName(product);
      productData.index = getPosition(product, list);
      productData.quantity = 1;

      launchProductClick(productData);
      setHistoryStateAndContinue(productId, url);
    });
  }
};

const launchProductTileVariantClick = (productVariant, productTile, list) => {
  const productId = productVariant.dataset.pid || null;

  if (productId) {
    getInfoProduct(productId).then((productData) => {
      productData.list = getListName(productTile);
      productData.index = getPosition(productTile, list);
      productData.quantity = 1;

      pushOnDataLayer({
        event: gtmEventName.viewItemList,
        ecommerce: {
          items: [productData]
        }
      });
    });
  }
};

const accumulateViewProductVariant = (product, list) => {
  const productId = product.dataset.vgid || product.dataset.pid;
  const position = getPosition(product, list);
  const listName = getListName(product);

  getInfoProduct(productId).then((productData) => {
    productData.position = position;
    productData.list_id = listName;
    productData.quantity = 1;

    listOfProductVariantsViewedAccumulated.push(productData);
  });
};

async function launchViewProductVariant() {
  accumulationOfProductVariantsViewedIsRunning = true;

  setTimeout(
    () => (accumulationOfProductVariantsViewedIsRunning = false),
    waitingTimeToLaunchViewProductVariant
  );

  await waitFor(canLaunchViewProductVariant).then(function () {
    pushOnDataLayer({
      event: gtmEventName.viewItemList,
      ecommerce: {
        items: listOfProductVariantsViewedAccumulated
      }
    });

    listOfProductVariantsViewedAccumulated = [];
    accumulationOfProductVariantsViewedIsRunning = false;
  });
}

const launchClickProductVariant = (product, list) => {
  const productId = product.dataset.pid;
  const position = getPosition(product, list);
  const listName = getListName(product);

  getInfoProduct(productId).then((productData) => {
    productData.position = position;
    productData.list_id = listName;
    productData.quantity = 1;

    pushOnDataLayer({
      event: gtmEventName.viewItem,
      ecommerce: {
        items: [productData]
      }
    });

    launchProductClick(productData);
  });
};

const launchProductDetail = (productId) => {
  getInfoProduct(productId).then((productData) => {
    pushOnDataLayer({
      event: gtmEventName.viewItem,
      ecommerce: {
        items: [productData]
      }
    });
  });
};

const launchAddToCart = (productData, cartData, actionFieldData) => {
  actionFieldData.action = gtmActionName.add;

  pushOnDataLayer({
    event: gtmEventName.addToCart,
    ecommerce: {
      items: [productData],
      cart: cartData
    }
  });
};

const launchRemoveFromCart = (productData, cartData, actionFieldData) => {
  actionFieldData.action = gtmActionName.remove;

  pushOnDataLayer({
    event: gtmEventName.removeFromCart,
    ecommerce: {
      items: [productData],
      cart: cartData
    }
  });
};

const launchAddToWishlist = (productData, actionFieldData) => {
  actionFieldData.action = gtmActionName.add;

  pushOnDataLayer({
    event: gtmEventName.addToWishlist,
    ecommerce: {
      items: [productData]
    }
  });
};

const launchRemoveFromWishlist = (productData, actionFieldData) => {
  actionFieldData.action = gtmActionName.remove;

  pushOnDataLayer({
    event: gtmEventName.removeFromWishlist,
    ecommerce: {
      items: [productData]
    }
  });
};

const launchProductClick = (data) => {
  pushOnDataLayer({
    event: gtmEventName.selectItem,
    ecommerce: {
      items: [data]
    }
  });
};

const dispatchEventOnCustomerAreaPage = (eventName, data = {}) => {
  if (customerAreaPage) {
    customerAreaPage.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: data
      })
    );
  }
};

/**
 * Get product info
 * @param {string} productId - Product id
 * @returns {Promise}
 */
const getInfoProduct = async (productId) => {
  const params = new URLSearchParams();
  params.append('productId', productId);

  return await axios
    .post(urlToGetInfoOfProduct, params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then((response) => {
      return response.data?.productData || {};
    })
    .catch((error) => console.error(error));
};

/**
 * Get cart info
 * @returns {Promise}
 */
const getInfoCart = async () => {
  return await axios
    .post(urlToGetInfoOfCart)
    .then((response) => {
      return response.data?.cartData || {};
    })
    .catch((error) => console.error(error));
};

/**
 * Determine if the element is already observed
 * @param {HTMLElement} element - The element to check
 * @param {Array} list - The list to search in
 * @returns {Boolean} - True if the element is already observed
 */
const isObserved = (element, list) => list.includes(element);

/**
 * Add the element to the list of observed product tiles
 * @param {HTMLElement} element - The element to add into the list
 * @param {Array} list - The list to add the element
 * @returns {void}
 */
const addToList = (element, list) => {
  list.push(element);
};

/**
 * Observe the element
 * @param {HTMLElement} target - The target to observe
 * @param {Array} list - The list that contains the target
 * @param {string} type - The type of the target
 * @returns {void}
 */
const observeElement = (target, list, type) => {
  observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const product = entry.target;

        if (type === productType.tile) {
          accumulateViewProductTile(product, list);
          launchViewProductTile(product, list);
        } else if (type === productType.variant) {
          accumulateViewProductVariant(product, list);
          launchViewProductVariant(product, list);
        }

        observer.unobserve(product);
      }
    });
  }, optionsForIntersectionObserver);
  observer.observe(target);
};

/**
 * Listen the product tiles in the viewport
 * @param {Boolean} resetList - Reset the list of product tiles observed
 * @returns {void}
 */
const listenProductTiles = (resetList = false) => {
  const productTiles = document.querySelectorAll(selector.productTile);

  if (resetList) {
    listOfProductTilesObserved = [];
    listOfProductTileVariantsViewed = [];
  }

  if (productTiles.length > 0) {
    productTiles.forEach((productTile) => {
      if (!isObserved(productTile, listOfProductTilesObserved)) {
        addToList(productTile, listOfProductTilesObserved);
        observeElement(
          productTile,
          listOfProductTilesObserved,
          productType.tile
        );
        listenProductTileLinkClick(productTile, listOfProductTilesObserved);
        listenProductTileVariants(productTile, listOfProductTilesObserved);
      }
    });
  }
};

/**
 * Listen the product tile link/s
 * @param {HTMLElement} productTile - The product tile element
 * @param {Array} list - The list that contains the product tile
 * @returns {void}
 */
const listenProductTileLinkClick = (productTile, list) => {
  const productTileLinks = productTile.querySelectorAll(
    selector.productTileLink
  );

  if (productTileLinks.length > 0) {
    productTileLinks.forEach((productTileLink) => {
      if (
        productTileLink.closest('.js-cart__recommendations-addons') === null
      ) {
        listenDOMEvent(productTileLink, 'click', (event) => {
          const url = productTileLink.getAttribute('href');

          event.preventDefault();
          launchClickProductTile(productTile, list, url);
        });
      }
    });
  }
};

/**
 * Listen the product tile variants
 * @param {HTMLElement} productTile - The product tile element
 * @param {Array} list - The list that contains the product tile
 * @returns {void}
 */
const listenProductTileVariants = (productTile, list) => {
  const productTileVariantsList = productTile.querySelector(
    selector.productTileVariantsList
  );

  if (productTileVariantsList) {
    const productTileVariants = productTileVariantsList.querySelectorAll(
      selector.productTileVariant
    );

    if (productTileVariants.length > 0) {
      productTileVariants.forEach((productTileVariant) => {
        listenDOMEvent(productTileVariant, 'click', () => {
          if (
            !isObserved(productTileVariant, listOfProductTileVariantsViewed)
          ) {
            addToList(productTileVariant, listOfProductTileVariantsViewed);
            launchProductTileVariantClick(
              productTileVariant,
              productTile,
              list
            );
          }
        });
      });
    }
  }
};

/**
 * Listen the product variants
 * @returns {void}
 */
const listenProductVariants = () => {
  const productVariants = document.querySelectorAll(selector.productVariant);

  if (productVariants.length > 0) {
    productVariants.forEach((productVariant) => {
      if (!isObserved(productVariant, listOfProductVariantsObserved)) {
        addToList(productVariant, listOfProductVariantsObserved);
        if (!hasClassElement(productVariant, classes.gtm.avoidViewed)) {
          observeElement(
            productVariant,
            listOfProductVariantsObserved,
            productType.variant
          );
        }
        listenDOMEvent(productVariant, 'click', () => {
          launchClickProductVariant(
            productVariant,
            listOfProductVariantsObserved
          );
        });
      }
    });
  }
};

const launchClickButtonShowMoreResults = (number) => {
  pushOnDataLayer({
    event: gtmEventName.eventTracking,
    category: gtmCategoryName.moreResults,
    action: gtmActionName.click,
    label: number
  });
};

const getPageSize = () => {
  const gridFooter = document.querySelector('.js-grid-footer');
  const pageSize = gridFooter ? parseInt(gridFooter.dataset.pageSize) : 0;
  const pageNumber = gridFooter
    ? parseInt(gridFooter.dataset.pageNumber) + 1
    : 0;

  return pageSize * pageNumber;
};

const setHistoryStateAndContinue = (productId, url) => {
  const start = 0;
  const size = getPageSize();
  const searchParams = new URLSearchParams(window.location.search);
  const currentSort = document.querySelector('.js-sorting-button') || null;
  const currentSortSelected = currentSort
    ? currentSort.options[currentSort.selectedIndex]
    : null;

  if (size > 0) {
    if (productId) {
      searchParams.set('product', productId);
    }

    if (!searchParams.has('srule') && currentSortSelected) {
      searchParams.set(
        'srule',
        currentSortSelected?.dataset?.sortingRuleId.trim()
      );
    }

    searchParams.set('sz', size);
    searchParams.set('start', start);

    var newUrl = `${window.location.pathname}?${searchParams.toString()}`;

    window.history.replaceState({}, '', newUrl);
  }

  window.location.href = url;
};

const launchCustomerLoggedSuccessfully = (object = {}) => {
  const method = object.method || 'web';
  const place = object.place || 'account';
  const hashedEmail = object.hashedEmail || '';

  pushOnDataLayer({
    event: gtmEventName.login,
    method: method,
    place: place,
    email: hashedEmail
  });
};

const launchCustomerSignUpSuccessfully = (object = {}) => {
  const method = object.method || 'web';
  const place = object.place || 'account';
  const hashedEmail = object.hashedEmail || '';

  pushOnDataLayer({
    event: gtmEventName.signUp,
    method: method,
    place: place,
    email: hashedEmail
  });
};

const launchPageView = (array = []) => {
  array.forEach((data) => {
    pushOnDataLayer(data);
  });
};

const launchCheckoutBegin = () => {
  axios
    .post(
      globalVariables.endpoint.gtmUrlForCheckoutBegin,
      {},
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    .then((response) => {
      const responseData = response.data;
      const dataLayerInfo = responseData?.dataLayerInfo || {};

      pushOnDataLayer(dataLayerInfo);
    })
    .catch((error) => console.error(error));
};

const launchCheckoutAddShippingInfo = () => {
  axios
    .post(
      globalVariables.endpoint.gtmUrlForCheckoutAddShippingInfo,
      {},
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    .then((response) => {
      const responseData = response.data;
      const dataLayerInfo = responseData?.dataLayerInfo || {};

      pushOnDataLayer(dataLayerInfo);
    })
    .catch((error) => console.error(error));
};

const launchCheckoutAddPaymentInfo = () => {
  axios
    .post(
      globalVariables.endpoint.gtmUrlForCheckoutAddPaymentInfo,
      {},
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    .then((response) => {
      const responseData = response.data;
      const dataLayerInfo = responseData?.dataLayerInfo || {};

      pushOnDataLayer(dataLayerInfo);
    })
    .catch((error) => console.error(error));
};

const launchViewCart = (url = '') => {
  if (url) {
    axios
      .post(
        url,
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      )
      .then((response) => {
        const responseData = response.data;
        const dataLayerInfo = responseData?.dataLayerInfo || {};

        pushOnDataLayer(dataLayerInfo);
      })
      .catch((error) => console.error(error));
  }
};

const applyFilter = (button) => {
  let applyFilter = false;
  const filterId = button?.dataset?.id;
  const filterValue = button?.dataset?.presentationId;

  if (button.type === 'checkbox') {
    // for toggling filters
    if (button.checked) {
      applyFilter = true;
    }
  } else if (!hasClassElement(button, classes.is.selected)) {
    applyFilter = true;
  }

  if (applyFilter) {
    launchApplyFilter(filterId, filterValue);
  }
};

const launchApplyFilter = (filterId, filterValue) => {
  if (filterId && filterValue) {
    pushOnDataLayer({
      event: gtmEventName.applyFilter,
      filterId: filterId,
      filterValue: filterValue
    });
  }
};

const launchApplySorting = (label, sortingRuleId) => {
  if (label && sortingRuleId) {
    pushOnDataLayer({
      event: gtmEventName.applySorting,
      label: label,
      sortingRuleId: sortingRuleId
    });
  }
};

export {
  selector as gtmSelector,
  customEvent as gtmCustomEvent,
  pushOnDataLayer,
  dispatchEventOnCustomerAreaPage,
  getListName as gtmGetListName,
  listenProductTiles as gtmListenProductTiles,
  launchProductDetail as gtmLaunchProductDetail,
  listenProductVariants as gtmListenProductVariants,
  launchAddToCartProduct as gtmLaunchAddToCartProduct,
  launchRemoveFromCartProduct as gtmLaunchRemoveFromCartProduct,
  launchAddProductToWishlist as gtmLaunchAddProductToWishlist,
  launchRemoveProductFromWishlist as gtmLaunchRemoveProductFromWishlist,
  launchClickButtonShowMoreResults as gtmLaunchClickButtonShowMoreResults,
  launchCustomerLoggedSuccessfully as gtmLaunchCustomerLoggedSuccessfully,
  launchCustomerSignUpSuccessfully as gtmLaunchCustomerSignUpSuccessfully,
  gtmEventName,
  gtmCategoryName,
  gtmActionName,
  launchPageView as gtmLaunchPageView,
  launchCheckoutBegin as gtmLaunchCheckoutBegin,
  launchCheckoutAddShippingInfo as gtmLaunchCheckoutAddShippingInfo,
  launchCheckoutAddPaymentInfo as gtmLaunchCheckoutAddPaymentInfo,
  launchViewCart as gtmLaunchViewCart,
  applyFilter as gtmApplyFilter,
  launchApplyFilter as gtmLaunchApplyFilter,
  launchApplySorting as gtmLaunchApplySorting
};
